import { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, Radio, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { ArrowDownIcon, CloseXIcon } from 'icons';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { useAppSelector } from 'store';

import { IUserStatePost, useGetRegions } from 'modules/global';

import { Modal } from 'components';
import styles from 'components/auth/auth-modal.module.scss';

import { useUpdateUser } from '../hooks';

import AvatarChangeModal from './avatar-change-modal';

interface IProps {
  open: boolean;
  onClose: () => void;
  image?: string;
}

const ProfilInfoEditModal = ({ open, onClose, image }: IProps) => {
  const t = useTranslations();
  const [openAvatar, setOpenAvatar] = useState(false);
  const user = useAppSelector(state => state.user);
  const [form] = Form.useForm();
  const [isFormTouched, setIsFormTouched] = useState(false);
  const { regions, isLoading: regionsLoading } = useGetRegions({ isNotAll: true });
  const { mutate, isLoading } = useUpdateUser(onClose);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name: user.name,
        last_name: user.last_name,
        region_id: user.district?.name || undefined,
        birthday: user.birthday ? dayjs(user.birthday) : null,
        gender: user.gender
      });
    }
  }, [user, form]);

  const handleFormChange = () => {
    const fieldsTouched = form.isFieldsTouched();

    setIsFormTouched(fieldsTouched);
  };

  const onFinish = (values: IUserStatePost) => {
    const formData = new FormData();

    if (values.name) formData.append('name', values.name);
    if (values.last_name) formData.append('last_name', values.last_name);
    if (values.birthday) formData.append('birthday', dayjs(values.birthday).format('YYYY-MM-DD'));
    if (values.gender) formData.append('gender', values.gender);
    if (values.region_id) formData.append('address_id', values.region_id);

    mutate(formData);
  };

  return (
    <div>
      <Form onFinish={onFinish} onFieldsChange={handleFormChange} form={form}>
        <Modal
          footer={
            <Button
              loading={isLoading}
              htmlType="submit"
              onClick={() => form.submit()}
              disabled={!isFormTouched}
              className={`!w-full !border-none ${
                isFormTouched ? '!bg-green-500 !text-white' : '!bg-gray-300 !text-white'
              } !rounded-lg !py-5 !text-white`}
            >
              {t('profile.saqlash')}
            </Button>
          }
          open={open}
          centered
          onClose={onClose}
          width="421px"
          styles={{
            content: { padding: '16px', borderRadius: '12px' }
          }}
          closeIcon={
            <div className="flex items-center justify-center rounded-full bg-gray-800 p-1">
              <CloseXIcon size="12" />
            </div>
          }
          title={<span className="text-lg">{t('profile.left_side.malumotlarni_tahrirlash')}</span>}
        >
          <div className="my-5 flex flex-col items-center justify-center space-y-2">
            <Image
              alt={user.name}
              className="rounded-full"
              src={image || '/images/default-user.png'}
              width={112}
              height={112}
              title='Foydalanuvchining default rasmi'
            />
            <p onClick={() => setOpenAvatar(true)} className="cursor-pointer text-blue-500">
              {t('Avatarkani_ozgartirish')}
            </p>
          </div>

          <Form.Item
            name="name"
            rules={[
              {
                required: true,
                message: t('auth.first_name_validation_message')
              }
            ]}
          >
            <Input className="!h-13 hover:!bg-gray-100 !rounded-xl" size="large" placeholder={t('auth.first_name_placeholder')} />
          </Form.Item>
          <Form.Item name="last_name" rules={[{ required: true, message: t('auth.last_name_placeholder') }]}>
            <Input
              className="!h-13 hover:!bg-gray-100 !rounded-xl"
              size="large"
              placeholder={t('auth.last_name_validation_message')}
            />
          </Form.Item>
          <Row gutter={10} className="!items-center">
            <Col span={12}>
              <Form.Item
                name="region_id"
                rules={[
                  {
                    required: true,
                    message: t('auth.region_validation_message')
                  }
                ]}
              >
                <Select
                  suffixIcon={<ArrowDownIcon />}
                  size="large"
                  className={styles.customSelect}
                  placeholder={t('auth.region_placeholder')}
                  options={regions}
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  showSearch
                  loading={regionsLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="birthday"
                rules={[
                  {
                    required: true,
                    message: t('auth.birth_date_validation_message')
                  }
                ]}
              >
                <DatePicker
                  size="large"
                  style={{ width: '100%' }}
                  defaultValue={dayjs('2005-01-01')}
                  className={styles.customDatePicker}
                  disabledDate={current => current && current > dayjs().endOf('day')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="gender"
            rules={[
              {
                required: true,
                message: t('auth.gender_validation_message')
              }
            ]}
          >
            <Radio.Group
              className={styles.customRadioStyle}
              options={[
                { label: t('auth.gender_male_label'), value: 'erkak' },
                { label: t('auth.gender_female_label'), value: 'ayol' }
              ]}
            />
          </Form.Item>
        </Modal>
      </Form>

      <AvatarChangeModal open={openAvatar} onClose={() => setOpenAvatar(false)} />
    </div>
  );
};

export default ProfilInfoEditModal;
